"use client";

import * as React from "react";
import { motion, type Transition } from "framer-motion";
import { cn } from "../../../../lib/utils";

type MotionHighlightContextType = {
  activeValue: string;
  registerItem: (value: string, node: HTMLElement | null) => void;
  controlled?: boolean;
};

const MotionHighlightContext = React.createContext<MotionHighlightContextType | undefined>(undefined);

function useMotionHighlight(): MotionHighlightContextType {
  const context = React.useContext(MotionHighlightContext);
  if (!context) {
    throw new Error("useMotionHighlight must be used within a MotionHighlight");
  }
  return context;
}

type MotionHighlightProps = React.ComponentProps<"div"> & {
  children: React.ReactNode;
  value: string;
  className?: string;
  transition?: Transition;
  controlledItems?: boolean;
};

function MotionHighlight({
  children,
  value,
  className,
  transition = {
    type: "spring",
    stiffness: 200,
    damping: 25
  },
  controlledItems = false,
  ...props
}: MotionHighlightProps) {
  const [activeValue, setActiveValue] = React.useState<string>(value);
  const itemsRef = React.useRef(new Map<string, HTMLElement>());
  const [dimensions, setDimensions] = React.useState({ width: 0, height: 0, x: 0, y: 0 });

  const registerItem = React.useCallback((val: string, node: HTMLElement | null) => {
    if (node) {
      itemsRef.current.set(val, node);
    } else {
      itemsRef.current.delete(val);
    }
  }, []);

  React.useEffect(() => {
    if (controlledItems) {
      setActiveValue(value);
    }
  }, [value, controlledItems]);

  React.useEffect(() => {
    const activeNode = itemsRef.current.get(activeValue);
    if (!activeNode) return;

    const container = activeNode.parentElement;
    if (!container) return;

    const updateDimensions = () => {
      const containerRect = container.getBoundingClientRect();
      const activeRect = activeNode.getBoundingClientRect();

      setDimensions({
        width: activeRect.width,
        height: activeRect.height,
        x: activeRect.left - containerRect.left,
        y: activeRect.top - containerRect.top
      });
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(activeNode);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [activeValue]);

  return (
    <MotionHighlightContext.Provider value={{ activeValue, registerItem, controlled: controlledItems }}>
      <div className="relative" {...props}>
        {dimensions.width > 0 && (
          <motion.div
            className={cn("absolute rounded-sm", className)}
            initial={false}
            animate={{
              width: dimensions.width,
              height: dimensions.height,
              x: dimensions.x,
              y: dimensions.y
            }}
            transition={transition}
          />
        )}
        {children}
      </div>
    </MotionHighlightContext.Provider>
  );
}

type MotionHighlightItemProps = React.ComponentProps<"div"> & {
  value: string;
  children: React.ReactNode;
  className?: string;
};

function MotionHighlightItem({ value, children, className, ...props }: MotionHighlightItemProps) {
  const { registerItem } = useMotionHighlight();
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    registerItem(value, ref.current);
    return () => registerItem(value, null);
  }, [value, registerItem]);

  return (
    <div ref={ref} className={cn("relative", className)} {...props}>
      {children}
    </div>
  );
}

export { MotionHighlight, MotionHighlightItem, useMotionHighlight, type MotionHighlightContextType };
